import { useQueryClient } from "@tanstack/react-query";
import { keyBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import type { CashSettingsController } from "../../../api/src/cash-settings/cash-settings.controller";
import type {
  AccountCashSetting,
  CashSetting,
} from "../../../api/src/cash-settings/lib";
import type { Household } from "../../../api/src/households-base/households-base.service";
import type { UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import { useQueryAllAccounts } from "../clients/account/lib";
import { useQueryHouseholds } from "../clients/household/lib";
import ActionButton from "../components/ActionButton";
import { useErrorMessage } from "../components/FormError";
import SettlementWarning from "../components/SettlementWarning";
import SubmitButton from "../components/SubmitButton";
import { Table } from "../components/Table/Table";
import TabContainerWithTabs from "../components/TabContainer";
import {
  extractServerMessage,
  useAuthenticatedMutationAsync,
  useAuthenticatedQuery,
} from "../lib/api";
import { processFileResponse } from "../lib/file";
import {
  AccountCashSettingRow,
  useAccountCashSettingsTable,
} from "./AccountCashSettingsTable";
import CashNav from "./CashNav";
import { useQueryAccountCashSettings, useQueryCashSettings } from "./lib";

function useQueryExport() {
  return useAuthenticatedQuery<
    UnpackResponse<CashSettingsController["exportAccountsCashSettings"]>,
    void
  >({
    queryKey: ["cash-settings", "accounts", "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}

function useBulkAssignCashSettings() {
  return useAuthenticatedMutationAsync<{
    success: boolean;
    updatedCount: number;
  }>(
    `/v1/cash-settings/accounts`,
    async (data: { accountIds: number[]; cashSettingId: number | null }) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );
}

const AccountCashSettings = () => {
  const {
    isPending: isPendingAccounts,
    data: dataAccounts,
    reload: reloadAccounts,
  } = useQueryAllAccounts({
    includeBalances: true,
  });
  const { data: dataHouseholds } = useQueryHouseholds();
  const { isPending: isPendingCashSettings, data: dataCashSettings } =
    useQueryCashSettings();
  // The split percentage and cash split shown against an account are the
  // account's own, never the assigned cash setting's
  // (doc-cash-split-01a05d5f47df200b §4.1). Neither reaches this page with the
  // account: GET /accounts selects no cash_split column. This endpoint is the
  // one that carries them — `getAllAccountCashSettingsForFirm` selects
  // `a.cash_split_percent` and `a.cash_split_symbol` straight off the account
  // (cash-settings.service.ts:657-658), outside the CASE that resolves the
  // target columns through the cash setting. HouseholdCashBilling.tsx:239-243
  // reads the same endpoint for the same reason. Every page of it (APP-1651).
  const { data: dataAccountCashSettings, reload: reloadAccountCashSettings } =
    useQueryAccountCashSettings();

  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [selectedCashSettingId, setSelectedCashSettingId] = useState<
    number | "remove" | null
  >(null);

  const { setError, resetError, errorMessage } = useErrorMessage();
  const bulkAssignMutation = useBulkAssignCashSettings();
  const [isAssigning, setIsAssigning] = useState(false);
  const queryClient = useQueryClient();

  const householdsMap: Record<number, Household> = (
    dataHouseholds?.data ?? []
  ).reduce(
    (result, household) => ({
      ...result,
      [household.id]: household,
    }),
    {},
  );
  const cashSettingsMap: Record<number, CashSetting> = keyBy(
    dataCashSettings?.data ?? [],
    "id",
  );
  const accountCashSettingsMap: Record<number, AccountCashSetting> = keyBy(
    dataAccountCashSettings?.data ?? [],
    "accountId",
  );

  const combinedCashSettingsAccountsHouseholds = useMemo(
    () =>
      (dataAccounts?.data ?? []).map((account): AccountCashSettingRow => {
        const cashSetting =
          account.cashSettingId == null
            ? undefined
            : cashSettingsMap[account.cashSettingId];
        const household =
          account.householdId == null
            ? null
            : householdsMap[account.householdId];
        const accountCashSetting = accountCashSettingsMap[account.id];

        return {
          ...account,
          ...(cashSetting
            ? {
                cashTargetType: cashSetting.cashTargetType,
                cashTargetAbsolute: cashSetting.cashTargetAbsolute,
                cashTargetPercent: cashSetting.cashTargetPercent,
                cashTargetMinCondition: cashSetting.cashTargetMinCondition,
                cashTargetMinAbsolute: cashSetting.cashTargetMinAbsolute,
                cashTargetMinPercent: cashSetting.cashTargetMinPercent,
                cashTargetMaxCondition: cashSetting.cashTargetMaxCondition,
                cashTargetMaxAbsolute: cashSetting.cashTargetMaxAbsolute,
                cashTargetMaxPercent: cashSetting.cashTargetMaxPercent,
              }
            : {}),
          // Deliberately outside the block above: those seven fields resolve
          // through the assigned cash setting, these two never do.
          cashSplitPercent: accountCashSetting?.cashSplitPercent ?? null,
          cashSplitSymbol: accountCashSetting?.cashSplitSymbol ?? null,
          cashSettingId: cashSetting?.id ?? null,
          cashSettingName: cashSetting?.name,
          household,
        };
      }),
    [
      dataAccounts?.data,
      cashSettingsMap,
      accountCashSettingsMap,
      householdsMap,
    ],
  );

  const { table, rowSelection, setRowSelection } = useAccountCashSettingsTable({
    accountCashSettings: combinedCashSettingsAccountsHouseholds,
    enableSelection: true,
  });

  const selectedAccountIds = useMemo(() => {
    return Object.keys(rowSelection).map((id) => parseInt(id));
  }, [rowSelection]);

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExport();

  const onExport = useCallback(async () => {
    await refetchExport();
  }, [refetchExport]);

  const handleBulkAssignClick = useCallback(() => {
    if (selectedAccountIds.length === 0) {
      setError(
        "No Accounts selected, first select the accounts you would like to Bulk Assign",
      );
      return;
    }
    setSelectedCashSettingId(null);
    setShowBulkAssignModal(true);
  }, [selectedAccountIds.length, setError]);

  const handleModalClose = useCallback(() => {
    setShowBulkAssignModal(false);
    resetError();
  }, [resetError]);

  const handleBulkAssign = useCallback(async () => {
    if (!selectedCashSettingId) {
      setError("Please select a cash setting");
      return;
    }

    setIsAssigning(true);
    try {
      await bulkAssignMutation({
        accountIds: selectedAccountIds,
        cashSettingId:
          selectedCashSettingId === "remove" ? null : selectedCashSettingId,
      });

      setRowSelection({});
      setShowBulkAssignModal(false);

      // Invalidate and refetch accounts data
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      reloadAccounts();
      // The assign is not in the mutation cache; walk the account cash
      // settings again (`useQueryAccountCashSettings`, APP-1651).
      reloadAccountCashSettings();
      resetError();
    } catch (err) {
      // The api's own sentence, when it sent one. Bulk assignment refuses the
      // whole batch when any account would be left with a pair R11 or R15
      // refuses, and its message names every such account
      // (an-decision-01a0ef5f76bd52b3, `bulkAssignCashSettingToAccounts`). A
      // fixed body would tell the advisor the assignment failed without telling
      // them which accounts to fix.
      setError(
        extractServerMessage(err) ??
          "Failed to assign cash settings to accounts",
      );
      console.error("Bulk assign error:", err);
    } finally {
      setIsAssigning(false);
    }
  }, [
    selectedCashSettingId,
    selectedAccountIds,
    bulkAssignMutation,
    setRowSelection,
    reloadAccounts,
    reloadAccountCashSettings,
    setError,
    resetError,
  ]);

  const cashSettingsOptions = useMemo(() => {
    return [
      <option key="" value="">
        Select a cash setting
      </option>,
      <option key="remove" value="remove">
        Remove Cash Setting
      </option>,
      ...(dataCashSettings?.data ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((setting) => (
          <option key={setting.id} value={setting.id}>
            {setting.name}
          </option>
        )),
    ];
  }, [dataCashSettings?.data]);

  return (
    <TabContainerWithTabs tabs={CashNav}>
      <Row className="mb-3">
        <Col>
          <SettlementWarning />
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Export"
                icon="/icons/folded-list.svg"
                onClick={onExport}
                className="me-2"
                disabled={isLoadingExport}
              />
            </ButtonGroup>
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Bulk Assign Cash Settings"
                icon="/icons/assign.svg"
                onClick={handleBulkAssignClick}
                disabled={selectedAccountIds.length === 0}
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          {isPendingAccounts || isPendingCashSettings ? (
            <Loading />
          ) : (
            <Table table={table} />
          )}
        </Col>
      </Row>

      <Modal
        show={showBulkAssignModal}
        onHide={handleModalClose}
        aria-labelledby="bulk-assign-modal-title"
        scrollable={true}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="bulk-assign-modal-title">
            Assign Cash Setting
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Rendered in the modal, as HouseholdCashBilling renders it. This
              page used to render errorMessage nowhere, so a failed assignment
              showed the advisor nothing at all. */}
          {errorMessage}
          <Row className="mb-3">
            <Col>
              {selectedAccountIds.length} account
              {selectedAccountIds.length !== 1 ? "s" : ""} selected
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Select
                value={selectedCashSettingId ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedCashSettingId(
                    value === ""
                      ? null
                      : value === "remove"
                        ? "remove"
                        : Number(value),
                  );
                  resetError();
                }}
              >
                {cashSettingsOptions}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <ActionButton
            variant="secondary"
            label="Cancel"
            onClick={handleModalClose}
            disabled={isAssigning}
          />
          <SubmitButton
            label="Assign"
            onClick={handleBulkAssign}
            isSubmitting={isAssigning}
            disabled={!selectedCashSettingId}
          />
        </Modal.Footer>
      </Modal>
    </TabContainerWithTabs>
  );
};

export default AccountCashSettings;
